import { QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query';
import { isTransientFailure } from '@wundo/client/api.ts';
import { isSessionRefusal } from '@wundo/client/session.ts';

/*
 * How long an answer is worth keeping, before anything asks for it again. Half
 * a minute is short enough that a change made elsewhere shows up while staff
 * are still looking for it, and long enough that walking around the console
 * costs nothing.
 */
const STALE_AFTER_MS = 30_000;

const SESSION_QUERY_KEYS = ['currentUser', 'impersonatedBy'];

/* The guards read these themselves; re-running them on their own refusal would never stop. */
function isSessionQuery(queryKey: QueryKey): boolean {
  return queryKey.length === 1 && SESSION_QUERY_KEYS.includes(String(queryKey[0]));
}

/**
 * A refused page query means the session may have changed underneath this tab,
 * so the identity is refreshed in place before `recheckSession` re-runs the
 * route guards. Refreshed, not dropped: a page waiting on it would refetch and
 * be refused again. A refresh that is itself refused is dropped, so the guards
 * ask again and send the person to sign in.
 */
export function createQueryClient(recheckSession: () => void): QueryClient {
  async function refreshSession(): Promise<void> {
    await Promise.all(
      SESSION_QUERY_KEYS.map((key) =>
        queryClient.refetchQueries(
          { queryKey: [key], exact: true },
          { cancelRefetch: false },
        ),
      ),
    );

    for (const key of SESSION_QUERY_KEYS) {
      if (isSessionRefusal(queryClient.getQueryState([key])?.error)) {
        queryClient.removeQueries({ queryKey: [key], exact: true });
      }
    }

    recheckSession();
  }

  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!isSessionRefusal(error) || isSessionQuery(query.queryKey)) {
          return;
        }

        void refreshSession();
      },
    }),
    defaultOptions: {
      queries: {
        /* Refusals are verdicts: retrying a 4xx only delays the message it carries. */
        retry: (failureCount, error) => isTransientFailure(error) && failureCount < 3,
        staleTime: STALE_AFTER_MS,
      },
    },
  });

  return queryClient;
}
